"use client";

import { StarIcon } from "lucide-react";
import React, { useState } from "react";
import { cn } from "@/lib/utils";

interface RatingProps {
  value?: number;
  onChange?: (rating: number) => void;
  /** Precision of the stars. Must be greater than 0 and less than or equal to 1. */
  precision?: number;
  /** Maximum number of stars. Must be greater than 0 and an integer. */
  max: number;
  readOnly?: boolean;
  /** Optional size for the stars. Default is "md" */
  size?: "sm" | "md" | "lg" | "xl";
}

export const Rating: React.FC<RatingProps> = ({
  value = 0,
  onChange,
  precision = 1,
  max,
  readOnly,
  size = "md",
}) => {
  const [hoverRating, setHoverRating] = useState(0);

  const handleStarClick = (index: number) => {
    const newRating = index + 0.5;
    onChange?.(newRating);
  };

  const handleStarHover = (index: number) => {
    setHoverRating(index + 0.5);
  };

  const handleMouseLeave = () => {
    setHoverRating(0);
  };

  return (
    <div
      className="flex space-x-1"
      onMouseLeave={handleMouseLeave}
      role="radiogroup"
      aria-label="Rating"
    >
      {new Array(max).fill(null).map((_, index) => (
        <Star
          key={index}
          filled={Math.min(Math.max((hoverRating || value) - index, 0), 1)}
          onClick={
            readOnly
              ? undefined
              : (precision) => handleStarClick(index + precision)
          }
          onMouseEnter={
            readOnly
              ? undefined
              : (precision) => handleStarHover(index + precision)
          }
          precision={precision}
          size={size}
        />
      ))}
    </div>
  );
};

interface StarProps {
  className?: string;
  filled: number;
  onClick?: (precision: number) => void;
  onMouseEnter?: (precision: number) => void;
  precision: number;
  size?: "sm" | "md" | "lg" | "xl";
}

export const Star: React.FC<StarProps> = ({
  className,
  filled,
  onClick,
  onMouseEnter,
  precision,
  size = "md",
}) => {
  const count = 1 / precision;

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
    xl: "w-10 h-10",
  };

  return (
    <div className={cn(sizeClasses[size], "flex flex-row", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="relative overflow-hidden h-full"
          style={{
            width: `${precision * 100}%`,
          }}
        >
          <StarIcon
            className={cn(
              "relative h-full cursor-pointer",
              filled > precision * i
                ? "text-primary fill-primary"
                : "text-muted-foreground",
            )}
            style={{
              left: `${-100 * i}%`,
              width: `${100 / precision}%`,
            }}
            onClick={() => onClick?.(precision * i)}
            onMouseEnter={() => onMouseEnter?.(precision * i)}
          />
        </div>
      ))}
    </div>
  );
};
